<div class="termination-dialog">
  <div class="termination-dialog__header">
    <div>
      <div class="termination-dialog__eyebrow">{{ reviewLabel }}</div>
      <h2>{{ data.employeeIdentity.name }}</h2>
      <p *ngIf="data.employeeIdentity.email">{{ data.employeeIdentity.email }}</p>
    </div>
    <button mat-icon-button type="button" aria-label="Close termination review" (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div class="termination-dialog__preview-notice" *ngIf="!isProcessingAvailable">
    <mat-icon>visibility</mat-icon>
    <span>Preview only — no changes have been made</span>
  </div>
  <div class="termination-dialog__preview-notice" *ngIf="isProcessingAvailable">
    <mat-icon>fact_check</mat-icon>
    <span>Guided review — team removals still use the existing member Remove action</span>
  </div>

  <mat-divider></mat-divider>

  <div class="termination-dialog__content">
    <div class="termination-dialog__loading" *ngIf="isLoading">
      <mat-spinner [diameter]="28"></mat-spinner>
      <span>Loading review details</span>
    </div>

    <div class="termination-dialog__error" role="status" *ngIf="!isLoading && loadError">
      <div>
        <mat-icon>info_outline</mat-icon>
        <span>{{ loadError }}</span>
      </div>
      <button
        *ngIf="canRetry"
        mat-stroked-button
        type="button"
        [disabled]="isLoading"
        (click)="retryReview()"
      >
        Retry
      </button>
    </div>

    <ng-container *ngIf="!isLoading && !loadError">
      <section class="review-section">
        <h3>Termination evidence</h3>
        <dl class="review-grid">
          <div><dt>Provider</dt><dd>{{ terminationCase.providerLabel }}</dd></div>
          <div><dt>Review state</dt><dd>{{ reviewLabel }}</dd></div>
          <div><dt>Effective termination date</dt><dd>{{ (terminationCase.effectiveTerminationDate | date:'mediumDate':'UTC') || 'Not available' }}</dd></div>
          <div><dt>Match status</dt><dd>{{ statusLabel(terminationCase.matchStatus) }}</dd></div>
          <div><dt>Employment status</dt><dd>{{ statusLabel(terminationCase.employmentStatus) }}</dd></div>
          <div><dt>Payroll participation</dt><dd>{{ statusLabel(terminationCase.payrollParticipationStatus) }}</dd></div>
          <div *ngIf="terminationCase.safeErrorCode" class="review-grid__wide">
            <dt>Review code</dt><dd>{{ terminationCase.safeErrorCode }}</dd>
          </div>
        </dl>
        <div class="manual-review-note" *ngIf="requiresManualReview">
          <mat-icon>rule</mat-icon>
          <span>Manual review is required before any future processing decision.</span>
        </div>
      </section>

      <section class="review-section" *ngIf="impactPreview as preview">
        <h3>Company memberships ({{ preview.memberships.count }})</h3>
        <p class="section-help" *ngIf="preview.memberships.matchRequired">
          No Wallit employee is linked to this case. Membership impact cannot be resolved automatically.
        </p>
        <div class="evidence-list" *ngIf="preview.memberships.items.length; else noMemberships">
          <div class="evidence-row" *ngFor="let membership of preview.memberships.items">
            <div>
              <strong>{{ teamName(membership.teamId) }}</strong>
              <span>{{ membership.status || 'Status unavailable' }}</span>
            </div>
            <span>{{ membership.disabled ? 'Disabled membership' : 'Current membership' }}</span>
          </div>
        </div>
        <ng-template #noMemberships>
          <p class="empty-evidence">No exact-company memberships were found.</p>
        </ng-template>
      </section>

      <section class="review-section" *ngIf="impactPreview as preview">
        <h3>Active monthly allowance participation ({{ preview.monthlyAllowanceParticipation.count }})</h3>
        <p class="section-help">Active monthly allowance participation stored for this company.</p>
        <div class="evidence-list" *ngIf="preview.monthlyAllowanceParticipation.items.length; else noAllowances">
          <div class="evidence-row" *ngFor="let allowance of preview.monthlyAllowanceParticipation.items">
            <div>
              <strong>{{ teamName(allowance.teamId) }}</strong>
              <span>{{ statusLabel(allowance.status) }}</span>
            </div>
            <span>{{ allowance.activatedAt ? (allowance.activatedAt | date:'mediumDate':'UTC') : 'Date unavailable' }}</span>
          </div>
        </div>
        <ng-template #noAllowances>
          <p class="empty-evidence">No active monthly allowance participation was found.</p>
        </ng-template>
      </section>

      <section class="review-section" *ngIf="impactPreview as preview">
        <h3>Pending settlement evidence</h3>
        <div class="evidence-callout">
          <strong>Stored evidence only</strong>
          <span>Live Stripe ACH status was not checked.</span>
        </div>
        <div class="evidence-list" *ngIf="preview.pendingSettlement.teams.length; else noSettlements">
          <div class="evidence-row" *ngFor="let settlement of preview.pendingSettlement.teams">
            <div>
              <strong>{{ teamName(settlement.teamId) }}</strong>
              <span>{{ settlement.knownPendingSettlement ? 'Stored pending indicator found' : 'No stored pending indicator' }}</span>
            </div>
            <span>{{ settlement.pendingFundingSnapshotCount }} pending funding snapshot(s)</span>
          </div>
        </div>
        <ng-template #noSettlements>
          <p class="empty-evidence">No team-level stored pending-settlement indicators were found.</p>
        </ng-template>
      </section>

      <section class="review-section" *ngIf="impactPreview as preview">
        <h3>Payroll deduction evidence</h3>
        <div class="evidence-callout evidence-callout--manual">
          <strong>Current deduction obligation: Unknown</strong>
          <span>Stored evidence is for manual review and does not establish an active cancellation obligation.</span>
        </div>
        <div class="evidence-list" *ngIf="preview.payrollDeductions.items.length; else noDeductions">
          <div class="evidence-row" *ngFor="let deduction of preview.payrollDeductions.items">
            <div>
              <strong>{{ evidenceRecordLabel(deduction.recordType) }}</strong>
              <span>{{ identityEvidenceLabel(deduction.identityEvidence) }}</span>
            </div>
            <span>{{ teamName(deduction.teamId) }}</span>
          </div>
        </div>
        <ng-template #noDeductions>
          <p class="empty-evidence">No stored payroll-deduction evidence was found. Current obligation remains unknown.</p>
        </ng-template>
      </section>

      <section class="review-section processing-section" *ngIf="data.processingEnabled">
        <h3>Guided processing</h3>
        <div class="termination-dialog__error" role="status" *ngIf="processingLoadError">
          <div><mat-icon>info_outline</mat-icon><span>{{ processingLoadError }}</span></div>
          <button
            *ngIf="!isProcessingUnavailable"
            mat-stroked-button
            type="button"
            [disabled]="isLoading"
            (click)="retryReview()"
          >Refresh</button>
        </div>
        <ng-container *ngIf="processingReadiness as readiness">
          <div class="evidence-callout" *ngIf="readiness.payrollDeductionCancellation.state === 'actionable'">
            <strong>One payroll deduction can be canceled through the guided UKG Ready flow</strong>
            <span>The cancellation is effective today, cancels the associated Wallit subscription, and does not issue a refund.</span>
          </div>
          <div class="evidence-callout" *ngIf="readiness.payrollDeductionCancellation.state === 'submitted' || deductionCancellationStatus">
            <strong>Cancellation submitted</strong>
            <span>UKG Ready accepted the cancellation submission and the associated Wallit subscription was canceled. This is not an independent UKG verification.</span>
          </div>
          <div class="evidence-callout" *ngIf="canReviewTeamRemovals">
            <strong>{{ readiness.membershipCounts.remainingMembershipCount }} company membership(s) still require review</strong>
            <span>Use Remove on each highlighted Payroll Teams row. The final membership asks whether the employee is leaving the company.</span>
          </div>
          <div class="evidence-callout evidence-callout--manual" *ngIf="readiness.state === 'awaiting_final_removal'">
            <strong>Final removal is still required</strong>
            <span>Settlement must clear before the existing Finalize Removal action can succeed.</span>
          </div>
          <div class="evidence-list" *ngIf="readiness.blockers.length">
            <div class="evidence-row" *ngFor="let processingBlocker of readiness.blockers">
              <div><strong>{{ processingBlocker.message }}</strong></div>
              <span>{{ processingBlocker.code }}</span>
            </div>
          </div>
          <div class="evidence-callout" *ngIf="readiness.state === 'ready_to_complete'">
            <strong>Required local outcomes verified</strong>
            <span>The termination review can now be completed explicitly.</span>
          </div>
        </ng-container>
      </section>
    </ng-container>
  </div>

  <mat-divider></mat-divider>
  <div class="termination-dialog__footer">
    <button mat-raised-button color="primary" type="button" (click)="close()">Close</button>
    <button
      *ngIf="canMarkResolved"
      class="mark-resolved-button"
      mat-stroked-button type="button"
      [disabled]="isResolving || isResolveConfirmationPending"
      (click)="markResolved()"
    >{{ isResolving ? 'Marking resolved…' : 'Mark resolved' }}</button>
    <button
      *ngIf="canCancelPayrollDeduction"
      mat-stroked-button type="button"
      [disabled]="isCancellingDeduction || isDeductionCancellationConfirmationPending"
      (click)="cancelPayrollDeduction()"
    >{{ isCancellingDeduction ? 'Submitting cancellation…' : 'Cancel payroll deduction' }}</button>
    <button
      *ngIf="canReviewTeamRemovals"
      mat-raised-button color="primary" type="button"
      (click)="reviewTeamRemovals()"
    >Review team removals</button>
    <button
      *ngIf="canCompleteReview"
      mat-raised-button color="primary" type="button"
      [disabled]="isCompleting"
      (click)="completeReview()"
    >{{ isCompleting ? 'Completing…' : 'Complete termination review' }}</button>
  </div>
</div>
